import React, { useState } from 'react';

import './Users.detail.scss';
import { useQuery } from '@apollo/client';
import gqlSchemaLibrary from '../../services/graphql.schemas';
import { PatientAvatarUpload } from '../../visuals/Patient.avatar.uploader';
import SpinnerLoader from "../../visuals/SpinnerLoader";

import ItemMenu from '../../forms/menu/MenuComponent';
import {useParams, useNavigate} from "react-router-dom";

import UserDisableModal from './User.deactivate.dialog';
import { Subject } from 'rxjs';
import {getAddressFormat} from "../../utils/address/addressFormat";
import {useTranslation} from "react-i18next";
import {PageViewLog} from "../../services/interactions/page.view";
import AuditTrail, {PAGES} from "../../services/interactions/audit.trail";


function DemographicEntry({ label, type, value }) {
    const getValue = () => {
        if (type && type === 'array') {
            return (
                value.map((v, i) => (
                    <p key={i}>
                        {v}
                    </p>
                ))
            )
        }
        return (
            <p>{value}</p>
        );
    }
    return (
        <div className="entry">
            <p className="label">{label}</p>
            <div>{getValue()}</div>
        </div>
    )
}


function UsersDetail({ currentOrg, currentUser, match }) {
    let { clinicianId } = useParams();
    const {t} = useTranslation(['users'])
    const ROLE_OPTIONS = [
        { label: t('administrator'), value: 'admin' },
        { label: t('clinician'), value: 'clinician' },
        { label: t('staff'), value: 'staff' },
        { label: t('read-only'), value: 'read' },
        { label: t('command-only'), value: 'command-only' }
    ];
    const navigate = useNavigate();
    const [disableRequested, setDisableRequested] = useState(false);
    const [clinician, setClinician] = useState(null);

    const [closeMenuSubject] = useState(new Subject());

    useQuery(gqlSchemaLibrary.clinicians.queries.getClinician, {
        variables: {
            _id: clinicianId,
            email: ''
        },
        onCompleted: ({ getClinician }) => {
            console.log('clinician to edit: ', getClinician);
            if (!getClinician.organizationIds.includes(currentUser.selectedOrgId)) {
                navigate(-1);
            }
            // Update current inputs to edit clinician, disable inputs where necessary
            setClinician(getClinician);
        },
        fetchPolicy: 'cache-and-network'
    })

    const goToEditUser = () => {
        AuditTrail.sendLog(PAGES.USERS_DETAIL, {tag: 'button', event: 'onClick', action: 'click option edit user', user_id: clinician._id});
        navigate(`/users/edit/${clinician._id}`);
    }


    const joinAddress = (address) => {
        if (!address || (!!address && !address[0])) {
            return []
        }
        return getAddressFormat(address[0]);
    }

    const getPhone = (phone) => {
        if (!phone || !phone.number) {
            return '';
        }
        if (phone.number.search(/\+/) !== -1) {
            return phone.number;
        }
        return '+' + phone.code + ' ' + phone.number;
    }

    const closeDisable = () => {
        setDisableRequested(false);
        closeMenuSubject.next(true);
    }

    const closeDisableModified = (user) => {
        const clin = JSON.parse(JSON.stringify(clinician));
        clin.user = Object.assign({}, clin.user, user);
        setClinician(clin);
        setDisableRequested(false);
        closeMenuSubject.next(true);
    }

    const openDisable = () => {
        setDisableRequested(true);
    }

    const statusTags = () => {
        if (clinician.user.isDisabled) {
            return (
                <div className={`tag _disenrolled`}>
                    DISABLED
                </div>
            )
        }
        else {
            return ''
        }
    }

    if (!clinician) {
        return <SpinnerLoader />
    }
    const roleLabel = (clinician.role && ROLE_OPTIONS.find(ro => ro.value === clinician.role)) || { label: 'Read only' };

    return (
        <div id="UserDetail">
            <UserDisableModal  clinician={clinician} disableRequested={disableRequested} closeDisable={closeDisable} closeUpdateDisabled={closeDisableModified} />
            <div className='userBanner'>
                <div className="imageUploadHolder">
                    <PatientAvatarUpload bio={clinician.user.bio} hideIfNone={true} />
                </div>
                <div className="bio">
                    <h1>{clinician.user.bio.lastname}, {clinician.user.bio.firstname}</h1>
                    <div className="bioDetails">
                        <p>
                            {clinician.dateOfBirth ? (new Date()).getFullYear() - (new Date(clinician.dateOfBirth)).getFullYear() : '--'} YO
                        </p>
                    </div>
                    {statusTags()}
                </div>
            </div>
            <div className="userDetails">
                <div className={`demographics card ${!!clinician && clinician.user.isDisabled ? 'disabled' : ''}`}>
                    <div className="header">
                        <p className="title">
                            {t('demographics')}
                        </p>
                        {
                            currentUser.userRoles.userClinicians.create?
                                <ItemMenu actionWillRedirect={false} forceCloseSubject={closeMenuSubject}
                                          label={'User Options'} event={PAGES.USERS_DETAIL} eventParams={{user_id: clinician._id}}>
                                    <>
                                        <div className="_option" onClick={goToEditUser}>
                                            <p>{t('edit-user')}</p>
                                        </div>
                                        <div className="_option" onClick={openDisable}>
                                            <p>{!!clinician && clinician.user.isDisabled ? t('set-as-active') : t('set-as-inactive')}</p>
                                        </div>
                                    </>
                                </ItemMenu> : ''
                        }
                    </div>
                    <div className="table">
                        <div>
                            <DemographicEntry label="Role" value={roleLabel.label} />
                        </div>
                        <div>
                            {
                                clinician.phones.map((p, i) => (
                                    <DemographicEntry key={i} label={p.label} value={getPhone(p)} />
                                ))
                            }
                            <DemographicEntry label="Email" value={clinician.user.email} />
                            {
                                clinician.address && clinician.address.length !== 0?
                                    <DemographicEntry label="Address" type="array" value={joinAddress(clinician.address)} /> : ''
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PageViewLog(UsersDetail);
